import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ListChecks,
  PauseCircle,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { EmptyState, PageHeader } from '@/shared/ui';
import { useLocation } from '@/shared/hooks/useLocation';
import { useAgentActions } from '../hooks/useAgentActions';
import { useAgentAssignment } from '../hooks/useAgentAssignment';
import { useAgentMission, type AgentMissionEntry } from '../hooks/useAgentMission';
import { useAgentTasks } from '../hooks/useAgentTasks';
import { useEnabledAgentTypes } from '@/shared/hooks/useEnabledAgentTypes';
import { TaskWorkspace } from '../components/TaskWorkspace';
import { ConversationWorkspace } from '../components/ConversationWorkspace';
import { TaskListHeader } from '../components/TaskListHeader';
import { useFeatureFlag } from '@/shared/hooks/useFeatureFlag';
import { UnifiedTaskList, UnifiedTaskListHeader } from '../components/UnifiedTaskList';
import { AgentChip } from '../components/AgentChip';
import type { AgentAssignment, AgentTask, AgentTaskWithdrawalKind } from '../types';
import { useAuth } from '@/shared/hooks/useAuth';
import {
  buildUnifiedTaskItems,
  countUnifiedTaskViews,
  filterByAgent,
  filterUnifiedTaskItems,
  paginateUnifiedTaskItems,
  type UnifiedTaskItem,
  type UnifiedTaskView,
} from '../utils/unifiedTasks';
import {
  bannerPausedRoleKeys,
  missionRoleKeysToRead,
  visibleAgentRoleKeys,
  pausedRoleKeys,
  taskSourceGates,
} from '../utils/taskPageAgents';
import {
  ALL_AGENTS,
  agentDisplayName,
  effectiveAgentFilter,
  readStoredAgentFilter,
  resolveInboxAgents,
  writeStoredAgentFilter,
  type AgentIdentity,
} from '../utils/agentLabels';
import styles from './UnifiedTasksPage.module.css';

const PAGE_SIZE = 20;

// The middle view is the queue: everything in progress or planned. It keeps the 'work' key so
// the filtering and grouping helpers stay untouched.
const VIEW_OPTIONS: Array<{ value: UnifiedTaskView; label: string }> = [
  { value: 'attention', label: 'Needs attention' },
  { value: 'work', label: 'Queued' },
  { value: 'history', label: 'History' },
];

function safeStorage(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

function useCurrentTime(): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  return now;
}

function LoadingWorkspace() {
  return (
    <div className={styles.skeletonWorkspace} aria-label="Loading tasks">
      <div className={styles.skeletonColumn}>
        <div className={`${styles.skeletonLine} ${styles.skeletonLineShort}`} />
        <div className={`${styles.skeletonLine} ${styles.skeletonLineLong}`} />
        <div className={`${styles.skeletonLine} ${styles.skeletonLineMedium}`} />
        <div className={`${styles.skeletonLine} ${styles.skeletonLineLong}`} />
        <div className={`${styles.skeletonLine} ${styles.skeletonLineMedium}`} />
      </div>
      <div className={styles.skeletonColumn}>
        <div className={`${styles.skeletonLine} ${styles.skeletonLineMedium}`} />
        <div className={`${styles.skeletonLine} ${styles.skeletonLineLong}`} />
        <div className={`${styles.skeletonLine} ${styles.skeletonLineLong}`} />
        <div className={`${styles.skeletonLine} ${styles.skeletonLineMedium}`} />
      </div>
    </div>
  );
}

function ErrorNotice({
  source,
  hasSavedData,
  onRetry,
}: {
  source: 'tasks' | 'planned work' | 'assignment' | 'history';
  hasSavedData: boolean;
  onRetry: () => void;
}) {
  return (
    <div className={styles.errorPanel} role="alert">
      <AlertCircle className={styles.errorPanelIcon} size={17} aria-hidden />
      <div className={styles.errorContent}>
        <span className={styles.errorText}>
          {source === 'tasks'
            ? 'Some task data could not load'
            : source === 'planned work'
              ? 'Planned work could not load'
              : source === 'history'
                ? 'Task history could not load'
                : 'The task assignment could not load'}
          {hasSavedData ? '. Showing the data that did load.' : '.'}
        </span>
        <button type="button" className={styles.retryButton} onClick={onRetry}>
          <RefreshCw size={13} aria-hidden />
          Retry
        </button>
      </div>
    </div>
  );
}

function PausedBanner({ agentNames }: { agentNames: string[] }) {
  // Several agents can be paused at once, so the sentence has to agree with how many.
  const subject =
    agentNames.length > 1
      ? `${agentNames.slice(0, -1).join(', ')} and ${agentNames[agentNames.length - 1]} are`
      : `${agentNames[0] ?? 'This agent'} is`;
  return (
    <div className={styles.pausedBanner} role="status">
      <PauseCircle className={styles.pausedIcon} size={18} aria-hidden />
      <span className={styles.bannerContent}>
        <span className={styles.bannerTitle}>{subject} paused</span>
        <span>New work is paused. Existing drafts are still available.</span>
      </span>
    </div>
  );
}

function EmptyTaskPanel({
  title,
  children,
  icon = <CheckCircle2 size={22} aria-hidden />,
}: {
  title: string;
  children: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className={styles.emptyPanel}>
      <span className={styles.emptyPanelIcon}>{icon}</span>
      <span className={styles.emptyPanelTitle}>{title}</span>
      <span className={styles.emptyPanelCopy}>{children}</span>
    </div>
  );
}

function TaskDetail({
  item,
  locationId,
  busy,
  reviewBusy,
  onApprove,
  onReject,
  onReview,
  onRetryPublish,
  retryPublishBusy,
  onSkip,
  onCancel,
  onWithdraw,
  onBack,
  agents,
}: {
  item: UnifiedTaskItem;
  locationId: string;
  busy: boolean;
  reviewBusy: boolean;
  onApprove: (taskId: string) => void;
  onReject: (taskId: string, reason?: string) => void;
  onReview: (taskId: string) => void;
  onRetryPublish: (taskId: string) => void;
  retryPublishBusy: boolean;
  onSkip: (taskId: string, reason?: string) => void;
  onCancel: (taskId: string, reason?: string) => void;
  onWithdraw?: (taskId: string, kind: AgentTaskWithdrawalKind, reason?: string) => void;
  onBack: () => void;
  agents: readonly AgentIdentity[];
}) {
  const task = item.task;
  const agentName = agentDisplayName(item.agentType, agents);
  // Every task opens as a conversation by default. A practice switched to the classic view
  // (Command Center → Agents → Classic task view) keeps the tabbed view below.
  const conversationView = !useFeatureFlag('classic-agent-tasks');

  if (conversationView) {
    return (
      <ConversationWorkspace
        item={item}
        agents={agents}
        locationId={locationId}
        onBack={onBack}
        busy={busy}
        reviewBusy={reviewBusy}
        retryPublishBusy={retryPublishBusy}
        onApprove={onApprove}
        onReject={onReject}
        onReview={onReview}
        onRetryPublish={onRetryPublish}
        onSkip={onSkip}
        onCancel={onCancel}
        onWithdraw={onWithdraw}
      />
    );
  }

  return (
    <div className={styles.legacyDetail}>
      <button type="button" className={styles.backButton} onClick={onBack}>
        <span aria-hidden>←</span>
        Back to tasks
      </button>
      <header className={styles.legacyHeading}>
        <h2 className={styles.legacyHeadingTitle}>{item.title}</h2>
        <div className={styles.legacyHeadingMeta}>
          <AgentChip agentType={item.agentType} agents={agents} />
          {item.typeLabel !== item.title && (
            <>
              <span aria-hidden>·</span>
              <span>{item.typeLabel}</span>
            </>
          )}
          <span aria-hidden>·</span>
          <span>{item.statusLabel}</span>
        </div>
        {item.objective && (
          <details className={styles.legacyObjective}>
            <summary>What {agentName} was asked to do</summary>
            <p>{item.objective}</p>
          </details>
        )}
      </header>
      <div className={styles.legacyWorkspace}>
        <TaskWorkspace
          task={task}
          locationId={locationId}
          busy={busy}
          reviewBusy={reviewBusy}
          onApprove={onApprove}
          onReject={onReject}
          onReview={onReview}
          onRetryPublish={onRetryPublish}
          retryPublishBusy={retryPublishBusy}
          onSkip={onSkip}
          onCancel={onCancel}
          onWithdraw={onWithdraw}
          agentName={agentName}
        />
      </div>
    </div>
  );
}

function UnifiedTasksContent({
  locationId,
  assignment,
  enabledAgents,
  tasks,
  tasksLoading,
  tasksError,
  retryTasks,
  missions,
  missionLoading,
  missionError,
  retryMission,
}: {
  locationId: string;
  assignment: AgentAssignment;
  /** The agents this page shows: switched on for the practice (rollout flags) and hired here or
   *  with work here (visibleAgentRoleKeys). */
  enabledAgents: ReadonlySet<string>;
  tasks: AgentTask[];
  tasksLoading: boolean;
  tasksError: boolean;
  retryTasks: () => void;
  /** One entry per agent hired here, each with that agent's own mission. */
  missions: AgentMissionEntry[];
  missionLoading: boolean;
  missionError: boolean;
  retryMission: () => void;
}) {
  const now = useCurrentTime();
  const { approve, reject, review, retryPublish, skip, cancel, withdraw } =
    useAgentActions(locationId);
  // Withdraw is the agency's (an agency admin viewing this practice); practice users never see it.
  const { user } = useAuth();
  const isAgency = user?.role === 'agency_admin';
  // Conversation view (the default): the page header, view tabs and filters move into the list
  // column, so the conversation starts right under the practice bar. The classic view keeps them on top.
  const compact = !useFeatureFlag('classic-agent-tasks');
  const [view, setView] = useState<UnifiedTaskView>('attention');
  const [search, setSearch] = useState('');
  const [updatedToday, setUpdatedToday] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  // "Ask Joe" on the Community page lands here with ?task=<id>: open that task once, in its view.
  const [searchParams, setSearchParams] = useSearchParams();
  const taskParam = searchParams.get('task');

  // Which agents this practice can see (rollout flags) and who they are (assignment). The agent
  // filter offers exactly those; the stored choice is a per-viewer convenience in localStorage.
  const agents = useMemo<AgentIdentity[]>(
    () => resolveInboxAgents(assignment.agents, assignment.roles, enabledAgents),
    [assignment.agents, assignment.roles, enabledAgents],
  );
  const agentTypes = useMemo(() => {
    const known = agents.map((a) => a.roleKey);
    const unknown = [...enabledAgents].filter((key) => !known.includes(key));
    return [...known, ...unknown];
  }, [agents, enabledAgents]);
  const [storedAgentFilter, setStoredAgentFilter] = useState(() =>
    readStoredAgentFilter(safeStorage()),
  );
  const agentFilter = effectiveAgentFilter(storedAgentFilter, enabledAgents);
  const agentTypeFilter = agentFilter === ALL_AGENTS ? null : agentFilter;
  const changeAgentFilter = useCallback((value: string) => {
    setStoredAgentFilter(value);
    writeStoredAgentFilter(safeStorage(), value);
    setPage(1);
    setMobileDetailOpen(false);
  }, []);
  const agentNames = useMemo(
    () => agentTypes.map((key) => agentDisplayName(key, agents)),
    [agentTypes, agents],
  );
  // Only the agents this practice can see, each with its own mission — the filter's "All agents"
  // shows every one of them, and choosing one narrows to that agent alone.
  const visibleMissions = useMemo(
    () => missions.filter((entry) => enabledAgents.has(entry.roleKey)),
    [missions, enabledAgents],
  );
  const missionByRole = useMemo(
    () => new Map(visibleMissions.map((entry) => [entry.roleKey, entry.snapshot])),
    [visibleMissions],
  );
  const hasSearch = search.trim().length > 0;

  // One list: the task API returns legacy rows and engine work in the same shape.
  // The per-agent rollout gate, applied client-side as well as by the API: an item whose agent is
  // not switched on for this practice never renders.
  const items = useMemo(
    () =>
      buildUnifiedTaskItems(tasks).filter(
        (item) => item.agentType !== null && enabledAgents.has(item.agentType),
      ),
    [tasks, enabledAgents],
  );
  useEffect(() => {
    if (!taskParam) return;
    const item = items.find((entry) => entry.id === taskParam);
    if (!item) return;
    setView(
      item.stage === 'needs_attention' ? 'attention' : item.stage === 'done' ? 'history' : 'work',
    );
    setSearch('');
    setUpdatedToday(false);
    setPage(1);
    setSelectedId(item.id);
    setMobileDetailOpen(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('task');
        return next;
      },
      { replace: true },
    );
  }, [taskParam, items, setSearchParams]);
  // View counts follow the agent filter, so "Needs attention · 3" is three of the chosen agent's.
  const counts = useMemo(
    () => countUnifiedTaskViews(filterByAgent(items, agentTypeFilter)),
    [items, agentTypeFilter],
  );
  // The Filter menu's per-agent counts: tasks in the current view for each agent.
  const agentCounts = useMemo(() => {
    const out: Record<string, number> = {
      [ALL_AGENTS]: countUnifiedTaskViews(items)[view],
    };
    for (const key of agentTypes) {
      out[key] = countUnifiedTaskViews(filterByAgent(items, key))[view];
    }
    return out;
  }, [items, agentTypes, view]);

  const filteredItems = useMemo(
    () =>
      filterUnifiedTaskItems(items, {
        view,
        search,
        updatedToday,
        agentType: agentTypeFilter,
        now,
      }),
    [items, view, search, updatedToday, agentTypeFilter, now],
  );
  const pageData = useMemo(
    () => paginateUnifiedTaskItems(filteredItems, page, PAGE_SIZE),
    [filteredItems, page],
  );
  const visibleItems = pageData.items;
  const selected = visibleItems.find((item) => item.id === selectedId) ?? visibleItems[0] ?? null;

  const sourceLoading = tasksLoading || missionLoading;
  const sourceSettled = !sourceLoading;
  const hasSavedData = items.length > 0;
  const sourceError = tasksError || missionError;
  // "Paused" means an agent's assignment is paused, or its mission was paused. Those are the only
  // two switches left -- the engine itself has no on/off any more. The banner only claims a pause
  // when every HIRED agent currently shown is paused (RF S5) -- an agent switched on for the
  // practice but not hired here must not keep the banner away.
  const missionStatusByRole = useMemo(
    () =>
      new Map(
        [...missionByRole].map(([roleKey, snapshot]) => [roleKey, snapshot?.mission?.status]),
      ),
    [missionByRole],
  );
  const pausedAgents = useMemo(
    () => pausedRoleKeys({ candidateRoleKeys: agentTypes, assignment, missionStatusByRole }),
    [agentTypes, assignment, missionStatusByRole],
  );
  const shownAgentTypes = useMemo(
    () => (agentTypeFilter === null ? agentTypes : [agentTypeFilter]),
    [agentTypes, agentTypeFilter],
  );
  const bannerRoleKeys = useMemo(
    () =>
      bannerPausedRoleKeys({ shownRoleKeys: shownAgentTypes, assignment, paused: pausedAgents }),
    [shownAgentTypes, assignment, pausedAgents],
  );
  const paused = bannerRoleKeys.length > 0;
  const pausedAgentNames = useMemo(
    () => bannerRoleKeys.map((roleKey) => agentDisplayName(roleKey, agents)),
    [bannerRoleKeys, agents],
  );
  const noAssignment = !assignment.assigned && assignment.status !== 'paused';
  const busy =
    approve.isPending ||
    reject.isPending ||
    skip.isPending ||
    cancel.isPending ||
    withdraw.isPending;
  const unfilteredEmptyView = !hasSearch && !updatedToday && !sourceError && sourceSettled;

  const changeView = (nextView: UnifiedTaskView) => {
    setView(nextView);
    setPage(1);
    setMobileDetailOpen(false);
  };

  const changeSearch = (value: string) => {
    setSearch(value);
    setPage(1);
    setMobileDetailOpen(false);
  };

  const changeToday = () => {
    setUpdatedToday((active) => !active);
    setPage(1);
    setMobileDetailOpen(false);
  };

  const clearFilters = () => {
    setView('attention');
    setSearch('');
    setUpdatedToday(false);
    changeAgentFilter(ALL_AGENTS);
    setPage(1);
    setMobileDetailOpen(false);
  };

  const selectItem = (id: string) => {
    setSelectedId(id);
    setMobileDetailOpen(true);
  };

  const showDetail = (item: UnifiedTaskItem | null) => {
    if (!item) {
      return (
        <div className={styles.emptyWorkspace}>
          Select a task to review its draft or see its schedule.
        </div>
      );
    }
    // Every row (legacy or engine-backed) renders through TaskWorkspace.
    return (
      <TaskDetail
        key={item.id}
        item={item}
        locationId={locationId}
        busy={busy}
        reviewBusy={review.isPending}
        onApprove={(taskId) => approve.mutate(taskId)}
        onReject={(taskId, reason) => reject.mutate({ taskId, reason })}
        onReview={(taskId) =>
          review.mutate(taskId, { onSuccess: () => setMobileDetailOpen(false) })
        }
        onRetryPublish={(taskId) => retryPublish.mutate(taskId)}
        retryPublishBusy={retryPublish.isPending}
        onSkip={(taskId, reason) => skip.mutate({ taskId, reason })}
        onCancel={(taskId, reason) => cancel.mutate({ taskId, reason })}
        onWithdraw={
          isAgency ? (taskId, kind, reason) => withdraw.mutate({ taskId, kind, reason }) : undefined
        }
        onBack={() => setMobileDetailOpen(false)}
        agents={agents}
      />
    );
  };

  const searchControl = (
    <div className={styles.searchField}>
      <Search className={styles.searchIcon} size={16} aria-hidden />
      <input
        className={styles.searchInput}
        type="search"
        value={search}
        onChange={(event) => changeSearch(event.target.value)}
        placeholder="Search tasks and types"
        aria-label="Search tasks and types"
      />
      {search && (
        <button
          type="button"
          className={styles.searchClear}
          onClick={() => changeSearch('')}
          aria-label="Clear search"
        >
          <X size={14} aria-hidden />
        </button>
      )}
    </div>
  );
  const todayControl = (
    <button
      type="button"
      className={
        updatedToday ? `${styles.todayButton} ${styles.todayButtonActive}` : styles.todayButton
      }
      onClick={changeToday}
      aria-pressed={updatedToday}
    >
      Updated today
    </button>
  );
  const agentControl = (
    <>
      {agentTypes.length > 0 && (
        <div className={styles.agentFilter} role="group" aria-label="Filter by agent">
          {[ALL_AGENTS, ...agentTypes].map((key) => {
            const active = agentFilter === key;
            return (
              <button
                key={key}
                type="button"
                className={
                  active
                    ? `${styles.agentFilterButton} ${styles.agentFilterButtonActive}`
                    : styles.agentFilterButton
                }
                onClick={() => changeAgentFilter(key)}
                aria-pressed={active}
              >
                {key === ALL_AGENTS ? 'All agents' : <AgentChip agentType={key} agents={agents} />}
              </button>
            );
          })}
        </div>
      )}
    </>
  );
  const clearControl = (
    <>
      {(hasSearch || updatedToday || view !== 'attention' || agentTypeFilter !== null) && (
        <button type="button" className={styles.clearButton} onClick={clearFilters}>
          Clear filters
        </button>
      )}
    </>
  );
  const emptyPanel = (
    <EmptyTaskPanel
      icon={
        view === 'attention' && !hasSearch ? (
          <CheckCircle2 size={22} aria-hidden />
        ) : (
          <ListChecks size={22} aria-hidden />
        )
      }
      title={
        !unfilteredEmptyView
          ? 'No matching tasks'
          : view === 'attention'
            ? 'You are all caught up'
            : view === 'history'
              ? 'No past tasks yet'
              : 'No planned work'
      }
    >
      {view === 'attention' && unfilteredEmptyView
        ? counts.work > 0
          ? `${counts.work} item${counts.work === 1 ? '' : 's'} in ${VIEW_OPTIONS[1]!.label}.`
          : 'New drafts and updates will appear here.'
        : unfilteredEmptyView && view === 'history'
          ? 'Reviewed and completed tasks appear here.'
          : unfilteredEmptyView && view === 'work'
            ? 'New work will appear here when your agents add it.'
            : 'Try another search or clear the filters.'}
    </EmptyTaskPanel>
  );

  if (compact) {
    return (
      <div className={styles.page}>
        {paused && <PausedBanner agentNames={pausedAgentNames} />}
        {tasksError && (
          <ErrorNotice source="tasks" hasSavedData={hasSavedData} onRetry={retryTasks} />
        )}
        {missionError && (
          <ErrorNotice source="planned work" hasSavedData={hasSavedData} onRetry={retryMission} />
        )}
        {sourceLoading && !hasSavedData ? (
          <LoadingWorkspace />
        ) : (
          <div
            className={`${styles.workspace} ${styles.workspaceCompact} ${
              mobileDetailOpen ? styles.mobileDetailOpen : ''
            }`}
          >
            <section className={styles.listColumn} aria-label="Task list">
              <TaskListHeader
                views={VIEW_OPTIONS}
                view={view}
                counts={counts}
                onView={changeView}
                search={search}
                onSearch={changeSearch}
                agents={agents}
                agentTypes={agentTypes}
                agentFilter={agentFilter}
                agentCounts={agentCounts}
                onAgent={changeAgentFilter}
                updatedToday={updatedToday}
                onToggleToday={changeToday}
              />
              {sourceSettled && sourceError && !hasSavedData ? (
                <EmptyTaskPanel
                  icon={<AlertCircle size={22} aria-hidden />}
                  title="Tasks could not load"
                >
                  Retry the source above to load drafts and scheduled work.
                </EmptyTaskPanel>
              ) : pageData.total === 0 ? (
                emptyPanel
              ) : (
                <>
                  <UnifiedTaskList
                    items={visibleItems}
                    view={hasSearch ? 'history' : view}
                    selectedId={selected?.id ?? null}
                    onSelect={selectItem}
                    now={now}
                    agents={agents}
                  />
                  {pageData.hasMore && (
                    <button
                      type="button"
                      className={styles.loadMore}
                      onClick={() => setPage((current) => current + 1)}
                    >
                      Load 20 more
                    </button>
                  )}
                </>
              )}
            </section>
            <section className={styles.detailColumn} aria-label="Task details">
              {showDetail(selected)}
            </section>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Tasks"
        subtitle={
          agentNames.length === 1
            ? `See what needs your attention and what ${agentNames[0]} has checked.`
            : 'See what needs your attention and what your agents have checked.'
        }
      />
      {paused && <PausedBanner agentNames={pausedAgentNames} />}
      {tasksError && (
        <ErrorNotice source="tasks" hasSavedData={hasSavedData} onRetry={retryTasks} />
      )}
      {missionError && (
        <ErrorNotice source="planned work" hasSavedData={hasSavedData} onRetry={retryMission} />
      )}

      {sourceLoading && !hasSavedData ? (
        <LoadingWorkspace />
      ) : sourceSettled && sourceError && !hasSavedData ? (
        <EmptyTaskPanel icon={<AlertCircle size={22} aria-hidden />} title="Tasks could not load">
          Retry the source above to load drafts and scheduled work.
        </EmptyTaskPanel>
      ) : noAssignment && !hasSavedData ? (
        <EmptyTaskPanel icon={<ListChecks size={22} aria-hidden />} title="No tasks yet">
          Tasks appear here when your agents start work.
        </EmptyTaskPanel>
      ) : (
        <>
          <nav className={styles.viewBar} aria-label="Task views">
            {VIEW_OPTIONS.map((option) => {
              const active = view === option.value;
              const count = counts[option.value];
              return (
                <button
                  key={option.value}
                  type="button"
                  className={
                    active ? `${styles.viewButton} ${styles.viewButtonActive}` : styles.viewButton
                  }
                  onClick={() => changeView(option.value)}
                  aria-pressed={active}
                >
                  {option.label}
                  <span className={styles.viewCount}>{count}</span>
                </button>
              );
            })}
          </nav>

          <div className={styles.toolbar}>
            {searchControl}
            {todayControl}
            {agentControl}
            {clearControl}
            <span className={styles.resultsCount} aria-live="polite">
              {hasSearch
                ? 'Search results'
                : VIEW_OPTIONS.find((option) => option.value === view)?.label}
              {' · '}
              {`Showing ${visibleItems.length} of ${pageData.total}`}
            </span>
          </div>

          {pageData.total === 0 ? (
            emptyPanel
          ) : (
            <div
              className={`${styles.workspace} ${mobileDetailOpen ? styles.mobileDetailOpen : ''}`}
            >
              <section className={styles.listColumn} aria-label="Task list">
                <UnifiedTaskListHeader
                  label={
                    hasSearch
                      ? 'Search results'
                      : (VIEW_OPTIONS.find((option) => option.value === view)?.label ?? 'Tasks')
                  }
                  count={pageData.total}
                />
                <UnifiedTaskList
                  items={visibleItems}
                  view={hasSearch ? 'history' : view}
                  selectedId={selected?.id ?? null}
                  onSelect={selectItem}
                  now={now}
                  agents={agents}
                />
                {pageData.hasMore && (
                  <button
                    type="button"
                    className={styles.loadMore}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    Load 20 more
                  </button>
                )}
              </section>
              <section className={styles.detailColumn} aria-label="Task details">
                {showDetail(selected)}
              </section>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function TasksPage() {
  const { currentLocation } = useLocation();
  const assignmentQuery = useAgentAssignment();
  const assignment = assignmentQuery.data;
  const agentFlags = useEnabledAgentTypes();
  // Unhiring deletes the assignment row, so tasks must not gate on being hired-or-paused — only
  // missions (current planned work) keep that gate. See taskSourceGates.
  const sourceGates = taskSourceGates({
    assignmentResolved: assignmentQuery.isSuccess,
    assignment,
  });
  const tasksQuery = useAgentTasks(sourceGates.tasks);
  // Which agents' missions to read: the ones assigned here that this practice is allowed to see.
  // An assignment saved before a location could hold two agents has only the flat `roleKey`, and a
  // location with no agent list at all falls back to every agent switched on for the practice --
  // which is what a role-less read used to resolve to anyway.
  const missionRoleKeys = useMemo(
    () => missionRoleKeysToRead(assignment, agentFlags.enabled),
    [assignment, agentFlags.enabled],
  );
  const missionQuery = useAgentMission(missionRoleKeys, sourceGates.missions);
  // The agents this page shows: switched on for the practice AND hired here or with work here. The
  // rollout flag is practice-wide, so on its own it would list an agent never hired at this location.
  const visibleAgents = useMemo(
    () =>
      agentFlags.enabled
        ? visibleAgentRoleKeys({
            assignment,
            enabled: agentFlags.enabled,
            taskAgentTypes: tasksQuery.tasks.map((task) => task.agentType),
          })
        : null,
    [assignment, agentFlags.enabled, tasksQuery.tasks],
  );
  const locationId = currentLocation?.id ?? assignmentQuery.locationId;

  if (!locationId) {
    return (
      <EmptyState
        icon={<ListChecks size={22} aria-hidden />}
        title="No location selected"
        description="Select a location to view tasks."
      />
    );
  }

  if ((assignmentQuery.isLoading && !assignment) || agentFlags.enabled === null) {
    return <LoadingWorkspace />;
  }

  // Per-agent rollout: until the Command Center switches an agent on for this practice, the Tasks
  // experience stays dark here even when an agent is hired and working.
  if (agentFlags.enabled.size === 0) {
    return (
      <div className={styles.page}>
        <PageHeader title="Tasks" subtitle="Your agents' drafts and findings will appear here." />
        <EmptyTaskPanel
          icon={<ListChecks size={22} aria-hidden />}
          title="Tasks are not enabled yet"
        >
          Your PracticeNova team switches this on for your practice. Nothing is needed from you.
        </EmptyTaskPanel>
      </div>
    );
  }

  if (assignmentQuery.isError || !assignment) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Tasks"
          subtitle="Review drafts and follow the work your agents plan for this location."
        />
        <ErrorNotice
          source="assignment"
          hasSavedData={false}
          onRetry={() => void assignmentQuery.refetch()}
        />
      </div>
    );
  }

  return (
    <UnifiedTasksContent
      key={locationId}
      locationId={locationId}
      assignment={assignment}
      enabledAgents={visibleAgents ?? agentFlags.enabled}
      tasks={tasksQuery.tasks}
      tasksLoading={tasksQuery.isLoading}
      tasksError={tasksQuery.isError}
      retryTasks={() => void tasksQuery.refetch()}
      missions={missionQuery.missions}
      missionLoading={missionQuery.isLoading}
      missionError={missionQuery.isError}
      retryMission={() => missionQuery.refetch()}
    />
  );
}
